import { CaretLeftIcon } from '@phosphor-icons/react'
import { observer } from 'mobx-react-lite'
import type { ReactElement, ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { PageGutterContext } from 'app/components/layout/PageScroll'
import { SectionRail } from 'app/components/layout/SectionRail'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useStripMainGutter } from 'app/hooks/useMainGutter'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import { useRailSections } from 'app/hooks/useRailSections'
import type { UserJson } from 'app/types/entities'
import { useUser } from 'app/utils'
import { PAGE_RAILS } from 'lib/pageRails'
import type { RailConfig } from 'lib/railSections'

const RailWell = observer(function RailWell({
  user,
  rails,
  children,
}: {
  user: UserJson
  rails: RailConfig[]
  children: ReactNode
}): ReactElement {
  const sections = useRailSections(user, rails)
  const isMobile = useIsMobile()
  const { pathname } = useLocation()
  // The first rail with a section lit owns the page: Settings before Ops, so an Ops page under
  // /settings (no Settings section there) gets the Ops rail.
  const current = sections.find((rail) => rail.inSection)
  // A phone's home path is the grouped list, so the index route's redirect never mounts there.
  const phoneList = isMobile
    ? sections.find(({ rail, groups }) => pathname.replace(/\/$/, ``) === rail.homePath && groups.length > 0)
    : undefined
  useStripMainGutter(!!current || !!phoneList)

  if (phoneList)
    return (
      <div className="h-full overflow-y-auto px-4 pb-6 pt-4">
        <SectionRail title={phoneList.rail.title} groups={phoneList.groups} variant="list" />
      </div>
    )
  if (!current) return <>{children}</>

  return (
    // One well beside the primary nav, like Atlas: the rail stays put, the section scrolls.
    // The tint on the margin (desktop only) is what makes the well read as a panel instead of
    // blending into the page, especially in dark mode where bg-surface and the page match.
    <div className="flex h-full min-h-0 md:bg-surface-subtle md:p-4">
      <div className="flex min-h-0 flex-1 overflow-hidden bg-surface md:rounded-2xl md:border md:border-default">
        {!isMobile && (
          <div className="w-56 shrink-0 overflow-y-auto border-r border-default px-3 py-5">
            <SectionRail title={current.rail.title} groups={current.groups} variant="rail" />
          </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          {isMobile && (
            <Link
              to={current.rail.homePath}
              className="flex shrink-0 items-center gap-1 px-4 pt-3 text-body-sm text-subtle"
            >
              <CaretLeftIcon size={14} weight="bold" /> {current.rail.title}
            </Link>
          )}
          <PageGutterContext.Provider value="inset">
            <div className="min-h-0 flex-1">{children}</div>
          </PageGutterContext.Provider>
        </div>
      </div>
    </div>
  )
})

/**
 * A page with a rail (Settings, Ops) renders its sections inside one page-level well with a
 * grouped rail (grouped navigation on). Everything else, and every route with the toggle off,
 * passes straight through.
 */
export const RailFrame = observer(function RailFrame({ children }: { children: ReactNode }): ReactElement {
  const user = useUser()
  const grouped = useFeatureToggle(`grouped-navigation`)
  const { pathname } = useLocation()
  // Only rails whose routes can hold the path are read at all; most pages have none.
  const rails = PAGE_RAILS.filter((rail) => rail.routes.some((route) => route.test(pathname)))
  if (!grouped || !user || rails.length === 0) return <>{children}</>
  return (
    <RailWell user={user} rails={rails}>
      {children}
    </RailWell>
  )
})
